// SuppressAlertActorModal — quick-suppress modal opened from the Detection Alerts
// Actions column (v1.123.2), for Microsoft Defender `security_alert` pass-through alerts.
// Third sibling of SuppressAlertTitleModal / SuppressAlertDescriptionModal — matches on the
// alert's EVIDENCE ACTOR (userAccount.userPrincipalName → primaryAddress → upn), for
// Microsoft-substrate service accounts that produce non-actionable pass-through noise
// (origin: NT SERVICE\MSExchangeAdminApiNetCore provisioning the eDiscovery mailbox — a
// benign action Defender relays as a high-severity "Mailbox Permission Change"). Appends
// the entry to the FIRING rule's own `parameters_json.excluded_alert_actors` via the
// dedicated SuppressAlertActor endpoint (which works on PROD rules — a narrow list
// mutation, not a full rule edit).
//
// The blast radius = the firing rule's existing scope; the modal surfaces that explicitly
// before the analyst confirms. There is no per-tenant copy here by design — scope a rule
// to an MSP (or clone it) via the rule editor for that. Default mode is 'exact' (actor
// strings are stable identifiers, unlike variable descriptions).

import React, { useState } from 'react';
import {
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { suppressAlertActor } from '../../api/client';

function splitCount(csv) {
  return (csv || '').split(',').map((s) => s.trim()).filter(Boolean).length;
}

export default function SuppressAlertActorModal({ alert, ruleMeta, prefill, onClose }) {
  // Pre-filled from the alert's parsed security_alert evidence actor.
  const [actor, setActor] = useState((prefill?.actor || '').trim());
  const [mode, setMode] = useState('exact');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);

  const includeCount = splitCount(ruleMeta?.includedTenantIds);
  const excludeCount = splitCount(ruleMeta?.excludedTenantIds);
  const scopeText =
    includeCount > 0 ? `only ${includeCount} scoped tenant${includeCount === 1 ? '' : 's'}`
      : excludeCount > 0 ? `all tenants except ${excludeCount}`
        : 'ALL tenants';
  const scopeTone = includeCount > 0 ? 'text-sky-300' : 'text-amber-300';

  const trimmedActor = actor.trim();
  const blocker = !alert.ruleId ? 'This alert has no rule id.'
    : !trimmedActor ? 'Enter the actor to suppress.'
      : null;

  const handleApply = async () => {
    if (blocker) { setError(blocker); return; }
    setApplying(true);
    setError(null);
    try {
      await suppressAlertActor(alert.ruleId, { actor: trimmedActor, mode });
      setApplied(true);
    } catch (err) {
      setError(err.message || 'Failed to suppress alert actor');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Suppress this alert actor</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {alert.ruleName ? <span className="text-gray-400">{alert.ruleName}</span> : 'Defender pass-through rule'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {applied ? (
            <div className="p-4 bg-emerald-900/20 border border-emerald-700/40 rounded-lg text-sm text-emerald-200 flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Alert actor suppressed.</p>
                <p className="text-emerald-300/80 mt-1">
                  Pass-through alerts whose evidence actor {mode === 'contains' ? 'contains' : 'exactly matches'}{' '}
                  <span className="font-mono">“{trimmedActor}”</span> will no longer be raised by this rule.
                  Takes effect within ~2 minutes.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Blast radius */}
              <div className="p-3 bg-gray-800/60 border border-gray-700/60 rounded text-xs text-gray-400 flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-500" />
                <span>
                  This adds the actor to the firing rule directly — pass-through alerts naming this actor will be
                  suppressed for <span className={`font-medium ${scopeTone}`}>{scopeText}</span> this rule applies to
                  (no per-tenant copy). A known-malicious IP still alerts. Use this only for Microsoft-substrate
                  service accounts (e.g. <span className="font-mono">NT SERVICE\…</span>), never a real user.
                </span>
              </div>

              {/* Actor */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Alert actor</label>
                <textarea value={actor} onChange={(e) => setActor(e.target.value)} rows={2}
                  placeholder="e.g. NT SERVICE\MSExchangeAdminApiNetCore (Microsoft.Exchange.AdminApi.NetCore)" className="input-field w-full text-sm font-mono" />
                <p className="text-[11px] text-gray-500 mt-1">Matched case-insensitively after trimming whitespace, against every evidence actor on the alert. No wildcards / regex.</p>
              </div>

              {/* Match mode */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Match</label>
                <div className="space-y-1.5 text-sm">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="actorModeSel" checked={mode === 'exact'} onChange={() => setMode('exact')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      Exact actor
                      <span className="text-[11px] text-gray-500 block">Suppress only this exact actor (recommended for service accounts).</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="actorModeSel" checked={mode === 'contains'} onChange={() => setMode('contains')}
                      className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 mt-0.5" />
                    <span className="text-gray-300 min-w-0">
                      Contains
                      <span className="text-[11px] text-gray-500 block">Suppress any evidence actor that contains this text.</span>
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-red-300 text-sm flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-800">
          {applied ? (
            <button onClick={onClose} className="btn-primary px-4 py-1.5 text-sm">Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={applying} className="btn-secondary px-4 py-1.5 text-sm">Cancel</button>
              <button onClick={handleApply} disabled={applying || !!blocker} className="btn-primary px-4 py-1.5 text-sm flex items-center gap-2">
                {applying && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                Suppress actor
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
